import type { AssessmentEntry } from "@/types/assessment";
import type { ScaleEntry, ScaleLibrary } from "@/types/scale";

export function requiredEntryCodes(
  scaleLibrary: ScaleLibrary,
  entries: readonly AssessmentEntry[],
): string[] {
  const scores = new Map(entries.map((entry) => [entry.entryCode, entry.score]));
  const result: string[] = [];
  for (const domain of scaleLibrary.domains) {
    for (const subdomain of domain.subdomains) {
      for (const longTerm of subdomain.longTermGoals) {
        result.push(longTerm.code);
        const score = scores.get(longTerm.code);
        if (score === 0 || score === 1) {
          longTerm.children.forEach((shortTerm) => result.push(shortTerm.code));
        }
      }
    }
  }
  return result;
}

export function nextUnscoredEntryCode(
  orderedCodes: readonly string[],
  entries: readonly AssessmentEntry[],
  currentCode: string,
): string | undefined {
  const scores = new Map(entries.map((entry) => [entry.entryCode, entry.score]));
  const currentIndex = orderedCodes.indexOf(currentCode);
  if (currentIndex < 0) return undefined;
  return orderedCodes
    .slice(currentIndex + 1)
    .find((code) => scores.get(code) === null || scores.get(code) === undefined);
}

export function activityStepRequiredCodes(
  stepEntryCodes: readonly string[],
  scaleLibrary: ScaleLibrary,
  entries: readonly AssessmentEntry[],
): string[] {
  const stepCodes = new Set(stepEntryCodes);
  return requiredEntryCodes(scaleLibrary, entries).filter((code) => stepCodes.has(code));
}

export function isActivityStepComplete(
  stepEntryCodes: readonly string[],
  scaleLibrary: ScaleLibrary,
  entries: readonly AssessmentEntry[],
): boolean {
  const requiredCodes = activityStepRequiredCodes(stepEntryCodes, scaleLibrary, entries);
  if (requiredCodes.length === 0) return false;
  const scores = new Map(entries.map((entry) => [entry.entryCode, entry.score]));
  return requiredCodes.every((code) => {
    const score = scores.get(code);
    return score !== null && score !== undefined;
  });
}

export function entryTitle(entry: ScaleEntry): string {
  return entry.shortTermGoal?.title ?? entry.longTermGoal.title;
}
